import { ChevronUpIcon } from "@heroicons/react/24/outline"
import { Checkbox } from "antd"
import { Fragment, useMemo } from "react"
import dayjs from "moment"
import { DUPLICATE_TABLE_COLS } from "../../constants"
import type { DuplicateAction, UploadTimeSheetEmployee } from "../../types"
import { buildDuplicateKey, formatDate, formatTime } from "../../utils"
import ActionRadioCard from "./ActionRadioCard"

export interface DuplicateRow {
  key: string
  employee: UploadTimeSheetEmployee
  departmentId: string
  departmentName: string
}

interface Props {
  groupedDepts: [string, { name: string; rows: DuplicateRow[] }][]
  collapsedDepts: Record<string, boolean>
  selectedKeys: string[]
  resolutions: Record<string, DuplicateAction>
  onToggleCollapse: (deptId: string) => void
  onToggleDept: (deptKeys: string[]) => void
  onToggleEmployee: (employeeKeys: string[]) => void
  onSetResolution: (keys: string[], action: DuplicateAction) => void
}

const cellBorder = "border-b border-[#E2E8F0]"
const COLUMN_COUNT = DUPLICATE_TABLE_COLS.length

const employeeIdCell = (employeeId: number | string | null | undefined) => (
  <td className={`pl-4 py-3 align-middle ${cellBorder}`}>
    <span className="text-sm text-[#020618]">{employeeId || "—"}</span>
  </td>
)

interface PrecomputedDept {
  deptId: string
  name: string
  rows: DuplicateRow[]
  deptKeys: string[]
  employeeGroups: {
    employee: UploadTimeSheetEmployee
    rows: DuplicateRow[]
  }[]
}

const ConflictTable = ({
  groupedDepts,
  collapsedDepts,
  selectedKeys,
  resolutions,
  onToggleCollapse,
  onToggleDept,
  onToggleEmployee,
  onSetResolution,
}: Props) => {
  // Bucket rows by employee within each department once per `groupedDepts`
  // change, so toggling selection / collapse doesn't rebuild every Map.
  const precomputedDepts: PrecomputedDept[] = useMemo(
    () =>
      groupedDepts.map(([deptId, dept]) => {
        const employeeGroups = new Map<
          string,
          { employee: UploadTimeSheetEmployee; rows: DuplicateRow[] }
        >()
        for (const r of dept.rows) {
          const k = String(
            r.employee.employeeId ?? r.employee.record?.employeeId
          )
          if (!employeeGroups.has(k)) {
            employeeGroups.set(k, { employee: r.employee, rows: [] })
          }
          employeeGroups.get(k)!.rows.push(r)
        }
        return {
          deptId,
          name: dept.name,
          rows: dept.rows,
          deptKeys: dept.rows.map((r) => r.key),
          employeeGroups: Array.from(employeeGroups.values()),
        }
      }),
    [groupedDepts]
  )

  return (
    <table className="w-full table-fixed border-collapse border-spacing-0 font-circular">
      <colgroup>
        {DUPLICATE_TABLE_COLS.map((w, i) => (
          <col
            key={i}
            style={{ width: w }}
          />
        ))}
      </colgroup>
      <thead>
        <tr className="bg-[#F8FAFC]">
          <th className="pl-4 py-3 border-b border-r border-[#E2E8F0]" />
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Employee ID
          </th>
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Date
          </th>
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Clock-in
          </th>
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Clock-out
          </th>
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Shift period ID
          </th>
          <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {precomputedDepts.length === 0 ? (
          <tr>
            <td
              colSpan={COLUMN_COUNT}
              className="px-6 py-6 text-sm text-[#62748E]"
            >
              No conflicts in this view.
            </td>
          </tr>
        ) : (
          precomputedDepts.map((dept, deptIdx) => {
            const { deptId, deptKeys, employeeGroups: employeeGroupsArr } = dept
            const collapsed = !!collapsedDepts[deptId]
            const deptAllSelected =
              deptKeys.length > 0 &&
              deptKeys.every((k) => selectedKeys.includes(k))

            return (
              <Fragment key={deptId}>
                {deptIdx > 0 && (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      className="h-3 bg-[#F1F5F9] p-0"
                    />
                  </tr>
                )}
                {/* Dept header row */}
                <tr className="bg-white">
                  <td className="pl-4 py-4 align-middle border-b border-r border-[#E2E8F0]">
                    <Checkbox
                      checked={deptAllSelected}
                      indeterminate={
                        !deptAllSelected &&
                        deptKeys.some((k) => selectedKeys.includes(k))
                      }
                      onChange={() => onToggleDept(deptKeys)}
                    >
                      <span className="text-sm font-circular text-[#020618]">
                        Select dept.
                      </span>
                    </Checkbox>
                  </td>
                  <td
                    colSpan={COLUMN_COUNT - 2}
                    className="pl-4 py-4 text-sm font-medium text-[#020618] align-middle border-b border-[#E2E8F0]"
                  >
                    {dept.name}
                  </td>
                  <td
                    colSpan={1}
                    className="pl-4 py-4 align-middle border-b border-[#E2E8F0]"
                  >
                    <button
                      type="button"
                      className="text-sm font-normal text-[#020618] inline-flex items-center gap-1"
                      onClick={() => onToggleCollapse(deptId)}
                    >
                      {collapsed ? "Expand" : "Collapse"}
                      <ChevronUpIcon
                        className={`w-3.5 h-3.5 transition-transform ${
                          collapsed ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </td>
                </tr>

                {!collapsed &&
                  employeeGroupsArr.flatMap((group) => {
                    const e = group.employee
                    const groupKeys = group.rows.map((r) => r.key)
                    const groupSelected =
                      groupKeys.length > 0 &&
                      groupKeys.every((k) => selectedKeys.includes(k))
                    const action = resolutions[groupKeys[0]]
                    const groupKey = `${deptId}-${
                      e.employeeId ?? e.record?.employeeId
                    }`
                    const totalRenderedRows = group.rows.length * 2

                    return group.rows.flatMap((r, dupIdx) => {
                      const ar = r.employee.affectedRecord
                      const rec = r.employee.record
                      const shiftPeriodId =
                        ar?.shiftPeriodId ?? rec?.shift_period_id
                      const isFirstOfGroup = dupIdx === 0
                      return [
                        // OLD record row
                        <tr key={`${r.key}-old`}>
                          {isFirstOfGroup && (
                            <td
                              rowSpan={totalRenderedRows}
                              className={`pl-4 py-3 align-middle border-r border-[#E2E8F0] ${cellBorder}`}
                            >
                              <Checkbox
                                checked={groupSelected}
                                indeterminate={
                                  !groupSelected &&
                                  groupKeys.some((k) =>
                                    selectedKeys.includes(k)
                                  )
                                }
                                onChange={() => onToggleEmployee(groupKeys)}
                              >
                                <span className="text-sm font-circular text-[#020618]">
                                  Select
                                </span>
                              </Checkbox>
                            </td>
                          )}
                          {employeeIdCell(
                            e.employeeId ?? e.record?.employeeId
                          )}
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {formatDate(ar?.clockIn)}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {formatTime(ar?.clockIn) || "—"}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {formatTime(ar?.clockOut) || "—"}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle border-r border-[#E2E8F0] ${cellBorder}`}
                          >
                            {shiftPeriodId ?? "—"}
                          </td>
                          {isFirstOfGroup && (
                            <td
                              rowSpan={totalRenderedRows}
                              className={`pl-4 py-3 align-middle ${cellBorder}`}
                            >
                              <div className="flex flex-col gap-2">
                                <ActionRadioCard
                                  name={`action-${groupKey}`}
                                  label="Overwrite old record"
                                  checked={action === "overwrite"}
                                  onSelect={() =>
                                    onSetResolution(groupKeys, "overwrite")
                                  }
                                />
                                <ActionRadioCard
                                  name={`action-${groupKey}`}
                                  label="Reject new record"
                                  checked={action === "reject"}
                                  onSelect={() =>
                                    onSetResolution(groupKeys, "reject")
                                  }
                                />
                              </div>
                            </td>
                          )}
                        </tr>,
                        // NEW record row
                        <tr key={`${r.key}-new`}>
                          {employeeIdCell(
                            e.employeeId ?? e.record?.employeeId
                          )}
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {rec?.date
                              ? dayjs(rec.date).format("MMM DD YYYY")
                              : "—"}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {rec?.clockin_time || "—"}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle ${cellBorder}`}
                          >
                            {rec?.clockout_time || "—"}
                          </td>
                          <td
                            className={`pl-4 py-3 text-sm text-[#020618] align-middle border-r border-[#E2E8F0] ${cellBorder}`}
                          >
                            {shiftPeriodId ?? "—"}
                          </td>
                        </tr>,
                      ]
                    })
                  })}
              </Fragment>
            )
          })
        )}
      </tbody>
    </table>
  )
}

export { buildDuplicateKey }
export default ConflictTable
